import { useEffect, useState } from 'react';
import type { SearchHit, SearchResults } from '@ddp/shared';

import { apiFetch } from '../../api/client.js';
import { track } from '../analytics/analytics.js';
import { useDebouncedValue } from './use-debounced-value.js';

/** Sous le seuil, on ne cherche pas : une lettre remonterait tout et n'aide personne. */
const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 250;

interface GlobalSearchState {
  hits: SearchHit[];
  loading: boolean;
  error: string | null;
  /** Vrai dès que le terme nettoyé atteint le seuil : le panneau doit alors s'ouvrir. */
  active: boolean;
}

/**
 * Recherche transverse (ADR-0015) pilotée par la frappe. Anti-rebond + annulation : une réponse
 * tardive à un terme déjà remplacé ne doit jamais écraser un résultat plus récent.
 */
export function useGlobalSearch(term: string): GlobalSearchState {
  const trimmed = term.trim();
  const debounced = useDebouncedValue(trimmed, DEBOUNCE_MS);
  const active = debounced.length >= MIN_QUERY_LENGTH;

  const [hits, setHits] = useState<SearchHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!active) {
      setHits([]);
      setLoading(false);
      setError(null);
      return;
    }

    let current = true;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const result = await apiFetch<SearchResults>(`/search?q=${encodeURIComponent(debounced)}`);
        // La course est gérée par `current` : seule la dernière requête montée écrit l'état.
        if (current) {
          setHits(result.hits);
          // Analytique (ADR-0026) : on mesure le **nombre** de résultats, jamais le terme cherché.
          track('SEARCH_PERFORMED', { results: result.hits.length });
        }
      } catch (err) {
        if (current) setError(err instanceof Error ? err.message : 'Recherche impossible');
      } finally {
        if (current) setLoading(false);
      }
    })();

    return () => {
      current = false;
    };
  }, [debounced, active]);

  return { hits, loading, error, active };
}
